import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "./Cart.css";

const CHECKOUT_FUNCTION_URL =
  "https://us-central1-ogphoto-app.cloudfunctions.net/createCheckoutSession";

export default function Cart() {
  const {
    cartItems,
    cartCount,
    cartTotal,
    removeFromCart,
    updateQuantity,
    clearCart,
  } = useCart();

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  /*
   * STRIPE CHECKOUT
   */

  const handleCheckout = async () => {
    if (cartItems.length === 0 || isCheckingOut) {
      return;
    }

    setIsCheckingOut(true);
    setCheckoutError("");

    try {
      const response = await fetch(CHECKOUT_FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: cartItems,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to start checkout.");
      }

      if (!data.url) {
        throw new Error("Stripe checkout URL was not returned.");
      }

      // Redirect customer to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error("Checkout error:", error);

      setCheckoutError(
        error.message || "Something went wrong. Please try again.",
      );

      setIsCheckingOut(false);
    }
  };

  /*
   * EMPTY CART
   */

  if (cartItems.length === 0) {
    return (
      <main className="cart-page">
        <header className="cart-header">
          <h1>Cart</h1>
          <span>0 ITEMS</span>
        </header>

        <section className="cart-empty">
          <p>Your cart is currently empty.</p>

          <Link to="/prints">VIEW PRINTS →</Link>
        </section>
      </main>
    );
  }

  /*
   * CART
   */

  return (
    <main className="cart-page">
      {/* HEADER */}

      <header className="cart-header">
        <h1>Cart</h1>

        <span>
          {cartCount} {cartCount === 1 ? "ITEM" : "ITEMS"}
        </span>
      </header>

      {/* CART CONTENT */}

      <section className="cart-content">
        <div className="cart-items">
          {cartItems.map((item, index) => (
            <article
              className="cart-item"
              key={`${item.printId}-${item.size}-${item.paper}-${item.frame}`}
            >
              {/* IMAGE */}

              <div className="cart-item-image">
                <img
                  src={item.photo}
                  alt={item.title || "Photograph"}
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>

              {/* DETAILS */}

              <div className="cart-item-details">
                <div className="cart-item-top">
                  <div>
                    <h2>{item.title || "Untitled"}</h2>

                    <span className="cart-item-number">PRINT</span>
                  </div>

                  <button
                    className="cart-remove"
                    onClick={() => removeFromCart(index)}
                  >
                    REMOVE
                  </button>
                </div>

                {/* OPTIONS */}

                <div className="cart-item-options">
                  <div>
                    <span>SIZE</span>
                    <p>{item.size}</p>
                  </div>

                  <div>
                    <span>PAPER</span>
                    <p>{item.paper}</p>
                  </div>

                  <div>
                    <span>FRAME</span>
                    <p>{item.frame}</p>
                  </div>
                </div>

                {/* BOTTOM */}

                <div className="cart-item-bottom">
                  <div className="cart-quantity">
                    <button
                      onClick={() => updateQuantity(index, item.quantity - 1)}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>

                    <span>{item.quantity}</span>

                    <button
                      onClick={() => updateQuantity(index, item.quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>

                  <div className="cart-item-price">
                    ${(item.price * item.quantity).toLocaleString()}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* SUMMARY */}

        <aside className="cart-summary">
          <div className="cart-summary-header">
            <span>ORDER SUMMARY</span>

            <button className="cart-clear" onClick={clearCart}>
              CLEAR CART
            </button>
          </div>

          <div className="cart-summary-row">
            <span>
              {cartCount} {cartCount === 1 ? "ITEM" : "ITEMS"}
            </span>

            <span>${cartTotal.toLocaleString()}</span>
          </div>

          <div className="cart-summary-row cart-summary-shipping">
            <span>SHIPPING</span>
            <span>CALCULATED AT CHECKOUT</span>
          </div>

          <div className="cart-summary-total">
            <span>TOTAL</span>

            <strong>${cartTotal.toLocaleString()}</strong>
          </div>

          {checkoutError && (
            <p className="cart-checkout-error" role="alert">
              {checkoutError}
            </p>
          )}

          <button
            className="cart-checkout-button"
            onClick={handleCheckout}
            disabled={isCheckingOut}
          >
            {isCheckingOut ? "REDIRECTING TO CHECKOUT..." : "CHECKOUT"}
          </button>

          <p className="cart-checkout-note">
            Secure checkout powered by Stripe.
          </p>

          <Link to="/prints" className="cart-continue">
            ← CONTINUE SHOPPING
          </Link>
        </aside>
      </section>
    </main>
  );
}
